import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./decisionList.css";
import Table from "../../core/common/dataTable/index";
import { DecisionTable } from "../../core/data/interface"; // À déclarer dans interface.ts
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AxiosError } from "axios";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  createDecision,
  deleteDecision,
  getAllDecisions,
  getDiplomeStat,
} from "../../api/diplome";
import { toast } from "sonner";
import AuthenticateDiplomeModal from "./authenticateDiplomeModal";

// ─── Boutons protégés ────────────────────────────────────────────────────────

const ProtectedBtnAddDecision = withPermission(
  (props: any) => (
    <div className="mb-2">
      <button
        onClick={() => props.createAlert()}
        className="btn btn-warning d-flex align-items-center"
      >
        <i className="ti ti-square-rounded-plus me-2" />
        Nouvelle Décision
      </button>
    </div>
  ),
  [buildPermission(Resource.DIPLOME, Action.CREATE)],
  "masquer",
);

const ProtectedBtnAuthenticateDiplome = withPermission(
  (props: any) => (
    <div className="mb-2 ms-2">
      <button
        onClick={() => props.setShowAuthenticateModal(true)}
        className="btn btn-primary d-flex align-items-center"
      >
        <i className="ti ti-shield-check me-2" />
        Authentification
      </button>
    </div>
  ),
  [buildPermission(Resource.DIPLOME_AUTH, Action.READ)],
  "masquer",
);

const ProtectedBtnDeleteDecision = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.showConfirmationAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.DIPLOME, Action.DELETE)],
  "masquer",
);

// ─── Helpers visuels ─────────────────────────────────────────────────────────

/**
 * Retourne les classes CSS Bootstrap correspondant au statut de la décision.
 */
const getStatutBadge = (statut: string) => {
  switch (statut) {
    case "Finalisé":
      return "badge badge-success d-inline-flex align-items-center";
    case "En cours de validation":
      return "badge badge-warning d-inline-flex align-items-center";
    case "Validé Partiellement":
      return "badge badge-info d-inline-flex align-items-center";
    case "Brouillon":
    default:
      return "badge badge-light d-inline-flex align-items-center text-muted";
  }
};

const STAT_CARDS = [
  {
    key: "Finalisé",
    label: "Décisions finalisées",
    icon: "ti ti-rosette-discount-check",
    accent: "success",
  },
  {
    key: "enAttente",
    label: "Décisions en attente",
    icon: "ti ti-clock-hour-4",
    accent: "warning",
  },
  {
    key: "diplomesDecernes",
    label: "Diplômés concernés",
    icon: "ti ti-school",
    accent: "primary",
  },
] as const;

const getStatutIcon = (statut: string) => {
  switch (statut) {
    case "Finalisé":
      return "ti-circle-check";
    case "En cours de validation":
      return "ti-clock";
    case "Validé Partiellement":
      return "ti-circle-x";
    case "Brouillon":
    default:
      return "ti-clock-pause";
  }
};

/**
 * Empile jusqu'à 3 avatars d'étudiants avec un compteur si besoin.
 */
const AvatarStack = ({
  students = [],
  count,
}: {
  students: { firstname?: string; lastname?: string }[];
  count: number;
}) => {
  const COLORS = [
    { bg: "#E6F1FB", color: "#0C447C" },
    { bg: "#E1F5EE", color: "#085041" },
    { bg: "#EEEDFE", color: "#3C3489" },
    { bg: "#FAECE7", color: "#712B13" },
  ];

  const getInitials = (firstname?: string, lastname?: string) => {
    const first = firstname?.trim()?.charAt(0)?.toUpperCase() || "";
    const last = lastname?.trim()?.charAt(0)?.toUpperCase() || "";
    return `${first}${last}` || "--";
  };

  const shown = students.slice(0, 3);
  const rest = count - 3;

  return (
    <div className="d-flex align-items-center gap-2">
      <div className="d-flex" style={{ marginLeft: 4 }}>
        {shown.map((student, i) => (
          <div
            key={i}
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: COLORS[i % COLORS.length].bg,
              color: COLORS[i % COLORS.length].color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 500,
              marginLeft: i > 0 ? -6 : 0,
              border: "2px solid white",
              zIndex: shown.length - i,
              position: "relative",
            }}
          >
            {getInitials(student.firstname, student.lastname)}
          </div>
        ))}

        {rest > 0 && (
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: "#F1EFE8",
              color: "#5F5E5A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              marginLeft: -6,
              border: "2px solid white",
              position: "relative",
            }}
          >
            +{rest}
          </div>
        )}
      </div>

      <span className="text-muted" style={{ fontSize: 13 }}>
        {count} étudiant{count > 1 ? "s" : ""}
      </span>
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const DecisionList = () => {
  const navigate = useNavigate();

  const [allDecisions, setAllDecisions] = useState<DecisionTable[]>([]);
  const [diplomeStat, setDiplomeStat] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [showAuthenticateModal, setShowAuthenticateModal] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    Finalisé: 0,
    enAttente: 0,
    diplomesDecernes: 0,
  });

  const MySwal = withReactContent(Swal);

  // ── Colonnes du tableau ──────────────────────────────────────────────────

  const columns = [
    {
      title: "N° Décision",
      dataIndex: "number",
      render: (number: string) => (
        <span
          className="fw-medium"
          style={{
            fontFamily: "monospace",
            fontSize: 13,
            background: "#eef4ff",
            color: "#1d4ed8",
            padding: "4px 10px",
            borderRadius: 999,
            border: "1px solid #dbe7ff",
            letterSpacing: "0.03em",
          }}
        >
          {number}
        </span>
      ),
      sorter: (a: DecisionTable, b: DecisionTable) =>
        a.number.localeCompare(b.number),
    },
    {
      title: "Nb étudiants",
      dataIndex: "total_students",
      render: (_: number, record: any) => (
        <AvatarStack
          students={record.students || []}
          count={record.total_students || 0}
        />
      ),
      sorter: (a: any, b: any) =>
        (a.total_students || 0) - (b.total_students || 0),
    },

    {
      title: "Créateur",
      dataIndex: "UserProfil",
      render: (user: DecisionTable["UserProfil"]) => (
        <div style={{ fontSize: 13 }}>
          <div className="fw-medium">
            {user.firstname} {user.lastname}
          </div>
          <div className="text-muted">{user.jobFunction}</div>
        </div>
      ),
      sorter: (a: DecisionTable, b: DecisionTable) =>
        `${a.UserProfil.firstname} ${a.UserProfil.lastname}`.localeCompare(
          `${b.UserProfil.firstname} ${b.UserProfil.lastname}`,
        ),
    },
    {
      title: "Statut",
      dataIndex: "statut",
      render: (statut: string) => (
        <span className={getStatutBadge(statut)}>
          <i className={`ti ${getStatutIcon(statut)} me-1`} />
          {statut}
        </span>
      ),
      sorter: (a: DecisionTable, b: DecisionTable) =>
        a.statut.localeCompare(b.statut),
    },
    // {
    //   title: "Validation",
    //   dataIndex: "validation",
    //   render: (validation: boolean[]) => (
    //     <ValidationSteps steps={validation} />
    //   ),
    // },
    {
      title: "Date de création",
      dataIndex: "createdAt",
      render: (date: string) =>
        new Date(date).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      sorter: (a: DecisionTable, b: DecisionTable) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: DecisionTable) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              {/* Voir le détail */}
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to={`/decision-details/${record.id}`}
                >
                  <i className="ti ti-eye me-2" />
                  Voir les détails
                </Link>
              </li>
              {/* Supprimer (protégé) */}
              <ProtectedBtnDeleteDecision
                showConfirmationAlert={showConfirmationAlert}
                record={record}
              />
            </ul>
          </div>
        </div>
      ),
    },
  ];

  // ── Création d'une nouvelle décision ──────────────────────────────────────────────────────────

  const createNewDecisionAlert = (decision: DecisionTable) => {
    MySwal.fire({
      title: "Créer une nouvelle décision ?",
      text: `Souhaitez-vous vraiment créer la décision ?`,
      showCancelButton: true,
      confirmButtonText: "Valider !",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Création de la décision en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          const response = await createDecision(
            buildPermissionContext(Resource.DIPLOME, Action.CREATE),
          );
          MySwal.fire({
            title: "Décision créée avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          // console.log("Décision créée : ", response.data);
          navigate(`/decision-details/${response?.data?.decision?.id}`);
        } catch (error) {
          const err = error as AxiosError<any>;
          MySwal.fire({
            title: "Erreur de création de la décision",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  // ── Suppression ──────────────────────────────────────────────────────────

  const showConfirmationAlert = (decision: DecisionTable) => {
    MySwal.fire({
      title: "Supprimer cette décision ?",
      text: `Souhaitez-vous vraiment supprimer la décision ${decision.number} ?`,
      showCancelButton: true,
      confirmButtonText: "Supprimer !",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          await deleteDecision(
            decision.id,
            buildPermissionContext(Resource.DIPLOME, Action.DELETE),
          );
          MySwal.fire({
            title: "Décision supprimée avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          reloadData();
        } catch (error) {
          const err = error as AxiosError<any>;
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const reloadData = () => setRefreshKey((prev) => prev + 1);

  // ── Chargement des données ───────────────────────────────────────────────

  const fetchAllDecision = async () => {
    try {
      const response = await getAllDecisions(
        buildPermissionContext(Resource.DIPLOME, Action.READ),
      );
      const decisons = response?.data ?? [];
      // console.log("Toutes les décisions", decisons);
      setAllDecisions(decisons);

      setHasLoaded(true);
      setIsLoading(false);
    } catch (error: any) {
      // console.log("erreur de chargement des décisions : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les décisions en cas d'erreur
      setAllDecisions([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const fetchAllDiplomeStat = async () => {
    try {
      const response = await getDiplomeStat(
        buildPermissionContext(Resource.DIPLOME, Action.READ),
      );
      setDiplomeStat(response?.data ?? null);
      // console.log("Statistiques des diplômes", response?.data);

      // Met à jour les stats pour les cartes
      setStats({
        Finalisé: response?.data?.decisions_finalisees || 0,
        enAttente: response?.data?.decisions_en_cours || 0,
        diplomesDecernes: response?.data?.diplomes_generes || 0,
      });
    } catch (error: any) {
      // console.log("erreur de chargement des décisions : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les décisions en cas d'erreur
      setDiplomeStat([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    setHasLoaded(false);
    setIsLoading(true);

    fetchAllDecision();
    fetchAllDiplomeStat();
  }, [refreshKey]);

  // ── Rendu ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="page-wrapper">
        <div className="content decision-list-page">
          {/* ── En-tête avec bouton ── */}
          <div className="decision-list-hero mb-4">
            <div className="d-md-flex d-block align-items-center justify-content-between gap-3">
              <div>
                <span className="decision-list-kicker">Diplômes</span>
                <h3 className="decision-list-title mb-1">
                  Décisions de diplomation
                </h3>
                <p className="decision-list-subtitle mb-0">
                  Suivez les validations, les décisions finalisées et les
                  cohortes d'étudiants concernées.
                </p>
              </div>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <ProtectedBtnAddDecision createAlert={createNewDecisionAlert} />
                <ProtectedBtnAuthenticateDiplome
                  setShowAuthenticateModal={setShowAuthenticateModal}
                />
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            {STAT_CARDS.map((card) => (
              <div key={card.key} className="col-12 col-md-6 col-xl-4">
                <div className={`decision-stat-card accent-${card.accent}`}>
                  <div className="decision-stat-icon">
                    <i className={card.icon} />
                  </div>
                  <div className="decision-stat-content">
                    <span className="decision-stat-label">{card.label}</span>
                    <h4 className="decision-stat-value mb-0">
                      {stats[card.key as keyof typeof stats]}
                    </h4>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Tableau ── */}
          <div className="decision-table-card card">
            <div className="decision-table-header card-header d-flex align-items-center justify-content-between flex-wrap">
              <div>
                <h4 className="mb-1">Liste des Décisions</h4>
                <p className="mb-0">
                  Une lecture plus claire des statuts, validations et cohortes.
                </p>
              </div>
              <span className="decision-table-count">
                {allDecisions.length} décision
                {allDecisions.length > 1 ? "s" : ""}
              </span>
            </div>
            <div className=" card-body p-0 py-3">
              {isLoading && !hasLoaded ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status" />
                  <span className="visually-hidden">
                    Chargement des données...
                  </span>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={allDecisions}
                  Selection={true}
                  // rowKey={(record: any) => record.id}
                  // onRow={(record: any) => ({
                  //   onClick: () => navigate(`/decision-details/${record.id}`),
                  //   style: { cursor: "pointer" },
                  // })}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <AuthenticateDiplomeModal
        show={showAuthenticateModal}
        onClose={() => setShowAuthenticateModal(false)}
      />
    </>
  );
};

export default DecisionList;
